"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { clearSearchCache } from "./searchUtils";
import { smartSearch } from "../lib/takeoff/itemSearch";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/20/solid";
import ItemDatabaseUpload from "./ItemDatabaseUpload";
import VendorPriceUpdate from "./VendorPriceUpdate";
import PriceUpdateHistory from "./PriceUpdateHistory";
import AlertDialog from "./AlertDialog";
import { useTabDirty } from "./TabRefreshContext";
const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Select styles ────────────────────────────────────────────────────────────

const selectStyles = {
  control: (p, state) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#1f2937",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "13px",
    borderRadius: "0.5rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#4b5563", fontSize: "13px" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected
      ? "#1f2937"
      : state.isFocused
        ? "#1a2332"
        : "#111827",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#1a2332" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    borderRadius: "0.5rem",
    border: "1px solid #1f2937",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

const multiSelectFilterStyles = {
  control: (p, state) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#1f2937",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "38px",
    fontSize: "13px",
    borderRadius: "0.5rem",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#4b5563", fontSize: "13px" }),
  menuPortal: (b) => ({ ...b, zIndex: 999999 }),
  menu: (b) => ({
    ...b,
    backgroundColor: "#111827",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
    border: "1px solid #1f2937",
    width: "300px",
    zIndex: 999999,
  }),
  menuList: (b) => ({
    ...b,
    maxHeight: "380px",
    padding: "8px",
    overflowY: "auto",
  }),
  option: (b, state) => ({
    ...b,
    backgroundColor: state.isFocused ? "#1f2937" : "#111827",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13px",
    "&:hover": { backgroundColor: "#1f2937" },
  }),
  valueContainer: (p) => ({
    ...p,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  }),
  multiValue: (b) => ({
    ...b,
    backgroundColor: "#1f2937",
    borderRadius: "4px",
    margin: 0,
    fontSize: "12px",
  }),
  multiValueLabel: (b) => ({ ...b, color: "#e5e7eb", padding: "2px 6px" }),
  multiValueRemove: (b) => ({
    ...b,
    color: "#6b7280",
    cursor: "pointer",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

// ─── Constants ────────────────────────────────────────────────────────────────

const UOM_OPTIONS = [
  { value: "EA", label: "EA – Each" },
  { value: "SF", label: "SF – Square Foot" },
  { value: "LF", label: "LF – Linear Foot" },
  { value: "SY", label: "SY – Square Yard" },
  { value: "CY", label: "CY – Cubic Yard" },
  { value: "GAL", label: "GAL – Gallon" },
  { value: "HR", label: "HR – Hour" },
  { value: "DAY", label: "DAY – Day" },
  { value: "TON", label: "TON – Ton" },
  { value: "LB", label: "LB – Pound" },
  { value: "BDL", label: "BDL – Bundle" },
  { value: "BOX", label: "BOX – Box" },
  { value: "ROLL", label: "ROLL – Roll" },
  { value: "SHEET", label: "SHEET – Sheet" },
  { value: "BAG", label: "BAG – Bag" },
];

const CATEGORY_OPTIONS = [
  { value: "M", label: "Material" },
  { value: "L", label: "Labor" },
];

const emptyItem = {
  itemCode: "",
  description: "",
  category: "M",
  costCode: "",
  sectionNumber: "",
  uom: "EA",
  size: "",
  coveragePerUnit: "",
  defaultCost: 0,
  defaultUnitRate: 0,
  defaultVendor: null,
  vendorPrices: [],
  itemNotes: "",
  manufacturer: "",
  partNumber: "",
  isActive: true,
  history: [],
  linkedItems: [], // chained items: [{ item, factor, calcMode, wastePct, note }]
};

// ─── Chained items ────────────────────────────────────────────────────────────
// Items that come into an estimate automatically with this one (board → screws, compound, tape, hanging labour…).
const CHAIN_MODES = [
  ["", "Item's default"],
  ["auto", "Auto"],
  ["takeoff", "Takeoff qty"],
  ["sf", "Area (SF)"],
  ["lf", "Length (LF)"],
  ["pieces", "Pieces (coverage)"],
  ["oc", "Studs O.C."],
  ["ea", "Each (count)"],
];
function ChainedItemsEditor({ value = [], onChange, items, selfId }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const byId = useMemo(
    () => new Map(items.map((i) => [String(i._id), i])),
    [items],
  );
  const taken = new Set(value.map((l) => String(l.item?._id || l.item)));
  const results = useMemo(
    () =>
      q.trim()
        ? smartSearch(items, q, { limit: 40 }).filter(
            (i) =>
              String(i._id) !== String(selfId) &&
              !taken.has(String(i._id)) &&
              i.isActive !== false,
          )
        : [],
    [q, items, selfId, value], // eslint-disable-line
  );
  const set = (k, patch) =>
    onChange(value.map((l, i) => (i === k ? { ...l, ...patch } : l)));
  const add = (it) => {
    onChange([
      ...value,
      {
        item: String(it._id),
        factor: 1,
        calcMode: "",
        wastePct: null,
        note: "",
      },
    ]);
    setQ("");
    setOpen(false);
  };
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        When this item is added to an assembly in an estimate, these come in
        with it. Each is calculated from the assembly the usual way (its own
        coverage / production rate) × the factor.
      </p>
      {value.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-800">
          <div className="grid grid-cols-12 gap-2 bg-gray-950/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <div className="col-span-4">Item</div>
            <div className="col-span-1">Factor ×</div>
            <div className="col-span-2">Quantity basis</div>
            <div className="col-span-1">Waste %</div>
            <div className="col-span-3">Note</div>
            <div className="col-span-1" />
          </div>
          {value.map((l, k) => {
            const it = byId.get(String(l.item?._id || l.item));
            return (
              <div
                key={k}
                className="grid grid-cols-12 items-center gap-2 border-t border-gray-800 px-3 py-2"
              >
                <div className="col-span-4 min-w-0">
                  {it ? (
                    <>
                      <div className="truncate text-sm text-gray-200">
                        {it.description}
                      </div>
                      <div className="truncate text-[11px] text-gray-500">
                        <span className="font-mono">{it.itemCode}</span> ·{" "}
                        {it.category === "L" ? "Labour" : "Material"} · {it.uom}
                        {it.size ? ` · ${it.size}` : ""}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-red-300">
                      Item no longer in the database
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={l.factor ?? 1}
                  onChange={(e) =>
                    set(k, {
                      factor:
                        e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  className={`${inputCls} col-span-1 !px-2 !py-1.5`}
                  title="Quantity factor: 2 = twice the quantity the assembly gives this item"
                />
                <select
                  value={l.calcMode || ""}
                  onChange={(e) => set(k, { calcMode: e.target.value })}
                  className={`${inputCls} col-span-2 !px-2 !py-1.5`}
                >
                  {CHAIN_MODES.map(([v, t]) => (
                    <option key={v} value={v}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="any"
                  value={l.wastePct ?? ""}
                  placeholder="—"
                  onChange={(e) =>
                    set(k, {
                      wastePct:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className={`${inputCls} col-span-1 !px-2 !py-1.5`}
                />
                <input
                  value={l.note || ""}
                  onChange={(e) => set(k, { note: e.target.value })}
                  placeholder="e.g. 1 box per 1,000 SF"
                  className={`${inputCls} col-span-3 !px-2 !py-1.5`}
                />
                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, i) => i !== k))}
                  className="col-span-1 justify-self-end rounded-lg p-1.5 text-gray-600 hover:bg-red-400/10 hover:text-red-400"
                  title="Remove"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div ref={boxRef} className="relative max-w-xl">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Chain another item — search screws, compound, tape, labour…"
          className={`${inputCls} pl-9`}
        />
        {open && results.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-xl border border-gray-700 bg-gray-900 shadow-2xl">
            {results.map((it) => (
              <button
                key={it._id}
                type="button"
                onClick={() => add(it)}
                className="flex w-full items-center gap-3 border-b border-gray-800 px-3 py-2 text-left hover:bg-gray-800"
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${it.category === "L" ? "bg-orange-500" : "bg-blue-500"}`}
                />
                <span className="w-28 shrink-0 truncate font-mono text-xs text-gray-400">
                  {it.itemCode}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-200">
                  {it.description}
                  {it.size ? (
                    <span className="text-gray-500"> · {it.size}</span>
                  ) : null}
                </span>
                <span className="text-xs text-gray-500">{it.uom}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Micro-components ─────────────────────────────────────────────────────────

const MoneyBadge = ({ value, color = "text-gray-300" }) => (
  <span className={`font-mono text-sm tabular-nums ${color}`}>
    ${(value || 0).toFixed(3)}
  </span>
);

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
    <span className="w-4 h-px bg-gray-700 inline-block" />
    {children}
    <span className="flex-1 h-px bg-gray-800 inline-block" />
  </p>
);

const FormField = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-400 mb-1.5">
      {label}
      {required && <span className="text-orange-500 ml-0.5">*</span>}
      {hint && (
        <span className="ml-1.5 text-[11px] text-gray-600 font-normal normal-case tracking-normal">
          {hint}
        </span>
      )}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-lg bg-gray-950 border border-gray-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 text-gray-200 text-sm px-3 py-2 outline-none transition-colors placeholder:text-gray-700";

const MoneyInput = ({ value, onChange, placeholder = "0.00" }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm select-none">
      $
    </span>
    <input
      type="number"
      step="0.01"
      value={value}
      onChange={onChange}
      className="w-full rounded-lg bg-gray-950 border border-gray-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 text-gray-200 text-sm pl-7 pr-3 py-2 outline-none transition-colors"
      placeholder={placeholder}
    />
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function ItemDatabaseManager() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPriceUpdate, setShowPriceUpdate] = useState(false);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    category: [],
    uom: [],
    sectionNumber: [],
    vendor: [],
    isActive: true,
  });
  const [editingItem, setEditingItem] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formData, setFormData] = useState({ ...emptyItem });
  const [vendorPriceForm, setVendorPriceForm] = useState({
    vendor: null,
    price: 0,
    unitRate: 0,
    notes: "",
    partNumber: "",
  });
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [showTableFilter, setShowTableFilter] = useState(null);
  useTabDirty(isAddingNew || !!editingItem);
  const filterCategoryRef = useRef(null);
  const filterSectionRef = useRef(null);
  const filterUomRef = useRef(null);
  const filterVendorRef = useRef(null);

  // Close filter dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!showTableFilter) return;
      const refs = {
        category: filterCategoryRef,
        sectionNumber: filterSectionRef,
        uom: filterUomRef,
        vendor: filterVendorRef,
      };
      const activeRef = refs[showTableFilter];
      if (activeRef?.current?.contains(e.target)) return;
      const isReactSelect =
        e.target.closest('[class*="css-"]') ||
        e.target.closest(".react-select__menu") ||
        e.target.id?.startsWith("react-select");
      if (isReactSelect) return;
      setShowTableFilter(null);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [showTableFilter]);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const [itemsRes, vendorsRes, costCodesRes] = await Promise.all([
        axios.get(`${API_BASE}/item-database?limit=5000`, { headers }),
        axios.get(`${API_BASE}/accounts/vendors`, { headers }),
        axios.get(`${API_BASE}/company-profile/cost-codes`, { headers }),
      ]);
      const loaded = itemsRes.data.items || [];
      clearSearchCache(loaded);
      setItems(loaded);
      setVendors(vendorsRes.data || []);
      setCostCodes(costCodesRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const showAlert = (type, title, message, onConfirm) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm: onConfirm || (() => setAlert((p) => ({ ...p, open: false }))),
    });

  // ─── Derived data ───────────────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    let r = items;
    if (filters.category.length)
      r = r.filter((i) => filters.category.includes(i.category));
    if (filters.uom.length) r = r.filter((i) => filters.uom.includes(i.uom));
    if (filters.sectionNumber.length)
      r = r.filter((i) =>
        filters.sectionNumber.includes(
          i.sectionNumber || i.costCode?.code || "",
        ),
      );
    if (filters.vendor.length) {
      const hasNone = filters.vendor.includes("__unassigned__");
      const ids = filters.vendor.filter((v) => v !== "__unassigned__");
      r = r.filter((i) => {
        const defId = i.defaultVendor?._id || i.defaultVendor || null;
        const vpIds = (i.vendorPrices || []).map(
          (vp) => vp.vendor?._id || vp.vendor,
        );
        return (
          (hasNone && !defId) ||
          (ids.length &&
            (ids.includes(defId) || ids.some((v) => vpIds.includes(v))))
        );
      });
    }
    if (filters.isActive !== null)
      r = r.filter((i) => i.isActive === filters.isActive);
    if (filters.search.trim()) {
      // trade-aware, ranked search (5/8 = .625, 20ga = 33mil, gwb = drywall, hat = furring…)
      const allowed = new Set(r.map((i) => i._id));
      r = smartSearch(items, filters.search, { limit: 100000 }).filter((i) =>
        allowed.has(i._id),
      );
    }
    return r;
  }, [items, filters]);

  const sectionOptions = useMemo(() => {
    const s = new Set();
    items.forEach((i) => {
      const v = i.sectionNumber || i.costCode?.code || "";
      if (v) s.add(v);
    });
    return Array.from(s)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [items]);

  const vendorOptions = vendors.map((v) => ({
    value: v._id,
    label: v.companyName,
  }));

  const costCodeOptions = costCodes.map((c) => {
    const rawCode = c.name ? c.code : c.code.split(" - ")[0].trim();
    return {
      value: c._id,
      label: c.name ? `${c.code} – ${c.name}` : c.code,
      code: rawCode,
    };
  });

  const vendorFilterOptions = useMemo(() => {
    const ids = new Set();
    let hasNone = false;
    items.forEach((i) => {
      const d = i.defaultVendor?._id || i.defaultVendor;
      if (d) ids.add(d);
      else hasNone = true;
      (i.vendorPrices || []).forEach((vp) => {
        const id = vp.vendor?._id || vp.vendor;
        if (id) ids.add(id);
      });
    });
    const opts = vendorOptions.filter((o) => ids.has(o.value));
    if (hasNone)
      opts.unshift({ value: "__unassigned__", label: "— No Default Vendor —" });
    return opts;
  }, [items, vendorOptions]);

  const getDefaultVendorPrice = (item) => {
    if (!item.defaultVendor || !item.vendorPrices?.length) return null;
    const defId = (item.defaultVendor?._id || item.defaultVendor)?.toString();
    return (
      item.vendorPrices.find(
        (vp) => (vp.vendor?._id || vp.vendor)?.toString() === defId,
      ) || null
    );
  };

  // ─── Form actions ───────────────────────────────────────────────────────────

  const handleStartAdd = () => {
    setFormData({ ...emptyItem });
    setIsAddingNew(true);
    setEditingItem(null);
    setShowVendorForm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStartEdit = (item) => {
    setFormData({
      ...item,
      costCode: item.costCode?._id || item.costCode || "",
      defaultVendor: item.defaultVendor?._id || item.defaultVendor || null,
      vendorPrices: item.vendorPrices || [],
      linkedItems: (item.linkedItems || []).map((l) => ({
        ...l,
        item: String(l.item?._id || l.item),
      })),
    });
    setEditingItem(item._id);
    setIsAddingNew(false);
    setShowVendorForm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setFormData({ ...emptyItem });
    setEditingItem(null);
    setIsAddingNew(false);
    setShowVendorForm(false);
  };

  const clearAllFilters = () =>
    setFilters({
      search: "",
      category: [],
      uom: [],
      sectionNumber: [],
      vendor: [],
      isActive: true,
    });

  const hasActiveFilters =
    filters.search ||
    filters.category.length ||
    filters.uom.length ||
    filters.sectionNumber.length ||
    filters.vendor.length ||
    filters.isActive !== true;

  const handleFormChange = (field, value) => {
    if (field === "costCode") {
      const sel = costCodeOptions.find((o) => o.value === value);
      setFormData((p) => ({
        ...p,
        costCode: value,
        sectionNumber: sel ? sel.code : "",
      }));
    } else {
      setFormData((p) => ({ ...p, [field]: value }));
    }
  };

  // Auto-compute coveragePerUnit from size for SF/LF items
  useEffect(() => {
    if (!["SF", "LF"].includes(formData.uom)) return;
    const size = (formData.size || "").trim();
    if (!size) return;
    const parseToFeet = (str) => {
      const s = String(str).trim();
      const frac = (f) => {
        const p = f.split("/");
        return p.length === 2
          ? parseFloat(p[0]) / parseFloat(p[1])
          : parseFloat(f) || 0;
      };
      const m1 = s.match(/^([\d.]+)'\s*([\d]+)[- ]([\d]+\/[\d]+)"?$/);
      if (m1) return parseFloat(m1[1]) + (parseFloat(m1[2]) + frac(m1[3])) / 12;
      const m2 = s.match(/^([\d.]+)'([\d.]+)"?$/);
      if (m2) return parseFloat(m2[1]) + (parseFloat(m2[2]) || 0) / 12;
      const m3 = s.match(/^([\d.]+)'?\s+([\d.]+)"?$/);
      if (m3) return parseFloat(m3[1]) + (parseFloat(m3[2]) || 0) / 12;
      if (s.endsWith("'")) return parseFloat(s) || 0;
      if (s.endsWith('"')) return (parseFloat(s) || 0) / 12;
      return parseFloat(s) || 0;
    };
    let computed = null;
    if (formData.uom === "SF") {
      const parts = size.replace(/×/g, "x").split(/\s*[xX]\s*/);
      if (parts.length === 2) {
        const w = parseToFeet(parts[0]),
          h = parseToFeet(parts[1]);
        if (w > 0 && h > 0) computed = parseFloat((w * h).toFixed(2));
      }
    } else if (formData.uom === "LF") {
      const len = parseToFeet(size);
      if (len > 0) computed = parseFloat(len.toFixed(2));
    }
    if (computed !== null)
      setFormData((p) => ({ ...p, coveragePerUnit: String(computed) }));
  }, [formData.size, formData.uom]);

  // ─── Vendor price actions ───────────────────────────────────────────────────

  const resetVendorForm = () =>
    setVendorPriceForm({
      vendor: null,
      price: 0,
      unitRate: 0,
      notes: "",
      partNumber: "",
    });

  const startEditVendor = (vendorId) => {
    const vp = formData.vendorPrices.find(
      (v) => (v.vendor?._id || v.vendor) === vendorId,
    );
    if (!vp) return;
    setEditingVendorId(vendorId);
    setVendorPriceForm({
      vendor: vendorId,
      price: vp.price || 0,
      unitRate: vp.unitRate || vp.price || 0,
      notes: vp.notes || "",
      partNumber: vp.partNumber || "",
    });
    setShowVendorForm(true);
  };

  const handleAddVendorPrice = () => {
    if (!vendorPriceForm.vendor) {
      showAlert("error", "Error", "Please select a vendor");
      return;
    }
    const entry = {
      vendor: vendorPriceForm.vendor,
      price: Number(vendorPriceForm.price),
      unitRate: Number(vendorPriceForm.unitRate || vendorPriceForm.price),
      notes: vendorPriceForm.notes || "",
      partNumber: vendorPriceForm.partNumber.trim() || "",
      lastUpdated: new Date(),
    };
    let updated = [...formData.vendorPrices];
    const idx = updated.findIndex(
      (vp) => (vp.vendor?._id || vp.vendor) === vendorPriceForm.vendor,
    );
    if (idx >= 0) updated[idx] = entry;
    else updated.push(entry);
    const newDefault =
      !formData.vendorPrices.length && !formData.defaultVendor
        ? vendorPriceForm.vendor
        : formData.defaultVendor;
    setFormData((p) => ({
      ...p,
      vendorPrices: updated,
      defaultVendor: newDefault,
    }));
    resetVendorForm();
    if (editingVendorId) {
      setEditingVendorId(null);
      setShowVendorForm(false);
    }
  };

  const handleRemoveVendorPrice = (vendorId) => {
    const updated = formData.vendorPrices.filter(
      (vp) => (vp.vendor?._id || vp.vendor) !== vendorId,
    );
    const wasDefault = formData.defaultVendor === vendorId;
    const newDefault = wasDefault
      ? updated.length
        ? updated[0].vendor?._id || updated[0].vendor
        : null
      : formData.defaultVendor;
    setFormData((p) => ({
      ...p,
      vendorPrices: updated,
      defaultVendor: newDefault,
    }));
  };

  // ─── Save / Delete ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formData.itemCode || !formData.description) {
      showAlert(
        "error",
        "Validation Error",
        "Item Code and Description are required",
      );
      return;
    }
    if (!formData.costCode) {
      showAlert("error", "Validation Error", "Cost Code / Section is required");
      return;
    }
    try {
      setIsSaving(true);
      if (isAddingNew)
        await axios.post(`${API_BASE}/item-database`, formData, { headers });
      else
        await axios.put(`${API_BASE}/item-database/${editingItem}`, formData, {
          headers,
        });
      await fetchItems();
      showAlert(
        "success",
        "Saved",
        isAddingNew ? "Item created successfully" : "Item updated successfully",
      );
      handleCancel();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save item",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (itemId, itemCode) => {
    showAlert(
      "warning",
      "Confirm Delete",
      `Delete item "${itemCode}"?`,
      async () => {
        try {
          await axios.delete(`${API_BASE}/item-database/${itemId}`, {
            headers,
          });
          setItems((p) => p.filter((i) => i._id !== itemId));
          showAlert("success", "Deleted", "Item removed");
        } catch {
          showAlert("error", "Error", "Failed to delete item");
        }
      },
    );
  };

  const handleToggleActive = async (item) => {
    try {
      const res = await axios.put(
        `${API_BASE}/item-database/${item._id}`,
        { ...item, isActive: !item.isActive },
        { headers },
      );
      setItems((p) => p.map((i) => (i._id === item._id ? res.data.item : i)));
    } catch {
      showAlert("error", "Error", "Failed to update item");
    }
  };

  const handleDownloadDatabase = async () => {
    try {
      const res = await axios.get(`${API_BASE}/item-database/download`, {
        headers,
      });
      if (!res.data.success) throw new Error();
      const { materialRows = [], laborRows = [] } = res.data;
      const VCOLS = [];
      for (let i = 1; i <= 10; i++)
        VCOLS.push(`Vendor${i}`, `Vendor${i}Price`, `Vendor${i}SKU`);
      const wb = XLSX.utils.book_new();
      if (materialRows.length)
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(materialRows, {
            header: [
              "itemCode",
              "description",
              "category",
              "sectionNumber",
              "uom",
              "size",
              "defaultCost",
              "defaultVendor",
              "defaultUnitRate",
              ...VCOLS,
              "manufacturer",
              "partNumber",
              "itemNotes",
              "isActive",
            ],
          }),
          "Material Items",
        );
      if (laborRows.length)
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(laborRows, {
            header: [
              "itemCode",
              "description",
              "category",
              "sectionNumber",
              "uom",
              "defaultUnitRate",
              "productionRate",
              "defaultVendor",
              ...VCOLS,
              "manufacturer",
              "partNumber",
              "itemNotes",
              "isActive",
            ],
          }),
          "Labor Items",
        );
      XLSX.writeFile(wb, "item_database_full.xlsx");
    } catch {
      showAlert("error", "Download Failed", "Could not download database");
    }
  };

  // ─── Loading state ──────────────────────────────────────────────────────────

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading item database…</p>
        </div>
      </div>
    );

  const isEditing = isAddingNew || !!editingItem;
  const isMaterialForm = formData.category === "M";
  const availableVendorOptions = vendorOptions.filter((o) =>
    formData.vendorPrices.some(
      (vp) => (vp.vendor?._id || vp.vendor) === o.value,
    ),
  );
  const activeCount = items.filter((i) => i.isActive).length;
  const materialCount = items.filter(
    (i) => i.category === "M" && i.isActive,
  ).length;
  const laborCount = items.filter(
    (i) => i.category === "L" && i.isActive,
  ).length;

  // ─── Filter header (table) ──────────────────────────────────────────────────

  const FilterHeader = ({
    label,
    filterKey,
    refObj,
    count,
    center = false,
    width = "",
  }) => (
    <th
      className={`px-3 py-3 text-xs font-semibold cursor-pointer relative uppercase tracking-wider select-none ${width} ${count > 0 ? "text-orange-400" : "text-gray-500 hover:text-gray-300"} ${center ? "text-center" : "text-left"}`}
      onClick={() =>
        setShowTableFilter((p) => (p === filterKey ? null : filterKey))
      }
    >
      <span
        className={`flex items-center gap-1 ${center ? "justify-center" : ""}`}
      >
        {label}
        <ChevronDownIcon
          className={`h-3 w-3 transition-transform ${showTableFilter === filterKey ? "rotate-180" : ""}`}
        />
        {count > 0 && (
          <span className="ml-0.5 px-1.5 py-0.5 text-[9px] bg-orange-500 text-white rounded-full font-bold leading-none">
            {count}
          </span>
        )}
      </span>
      {showTableFilter === filterKey && refObj && (
        <div
          ref={refObj}
          className="absolute left-0 top-full z-[99999] mt-1 rounded-lg shadow-2xl"
          style={{ minWidth: "260px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 bg-gray-950 border border-gray-800 rounded-lg">
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={
                filterKey === "category"
                  ? CATEGORY_OPTIONS
                  : filterKey === "uom"
                    ? UOM_OPTIONS
                    : filterKey === "sectionNumber"
                      ? sectionOptions
                      : vendorFilterOptions
              }
              value={
                filterKey === "category"
                  ? CATEGORY_OPTIONS.filter((o) =>
                      filters.category.includes(o.value),
                    )
                  : filterKey === "uom"
                    ? UOM_OPTIONS.filter((o) => filters.uom.includes(o.value))
                    : filterKey === "sectionNumber"
                      ? sectionOptions.filter((o) =>
                          filters.sectionNumber.includes(o.value),
                        )
                      : vendorFilterOptions.filter((o) =>
                          filters.vendor.includes(o.value),
                        )
              }
              onChange={(opts) =>
                setFilters((p) => ({
                  ...p,
                  [filterKey]: opts ? opts.map((o) => o.value) : [],
                }))
              }
              menuPortalTarget={document.body}
              styles={multiSelectFilterStyles}
              placeholder={`Filter by ${label.toLowerCase()}…`}
            />
          </div>
        </div>
      )}
    </th>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-full mx-auto px-4 py-6 space-y-4">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
        onConfirm={alert.onConfirm}
      />

      {/* ── Page header ── */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="p-1.5 bg-orange-500/10 rounded-lg">
                <CubeIcon className="h-4 w-4 text-orange-500" />
              </div>
              <h1 className="text-lg font-bold text-white tracking-tight">
                Item Database
              </h1>
            </div>
            <p className="text-sm text-gray-600">
              Reusable items for change orders and purchase orders
            </p>
            {!isEditing && (
              <div className="flex items-center gap-3 mt-3">
                <span className="text-xs text-gray-600">
                  <span className="font-semibold text-white">
                    {activeCount}
                  </span>{" "}
                  active
                </span>
                <span className="w-px h-3 bg-gray-800" />
                <span className="text-xs text-gray-600">
                  <span className="font-semibold text-blue-400">
                    {materialCount}
                  </span>{" "}
                  material
                </span>
                <span className="w-px h-3 bg-gray-800" />
                <span className="text-xs text-gray-600">
                  <span className="font-semibold text-orange-400">
                    {laborCount}
                  </span>{" "}
                  labor
                </span>
              </div>
            )}
          </div>
          {!isEditing && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadDatabase}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
              >
                <DocumentArrowDownIcon className="h-4 w-4" /> Export
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
              >
                <ArrowUpTrayIcon className="h-4 w-4" /> Import
              </button>
              <button
                onClick={() => setShowPriceUpdate(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg transition-colors"
              >
                <CurrencyDollarIcon className="h-4 w-4" /> Update Pricing
              </button>
              <button
                onClick={() => setShowPriceHistory(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
              >
                <ClockIcon className="h-4 w-4" /> Price History
              </button>
              <button
                onClick={handleStartAdd}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-sm shadow-orange-500/20"
              >
                <PlusIcon className="h-4 w-4" /> Add Item
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          FORM — New or Edit
      ══════════════════════════════════════════════════════════════════════ */}
      {isEditing && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          {/* Form header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-1 h-5 bg-orange-500 rounded-full" />
              <h2 className="text-sm font-semibold text-white">
                {isAddingNew ? "New Item" : "Edit Item"}
              </h2>
              {!isAddingNew && formData.itemCode && (
                <code className="px-2 py-0.5 text-xs text-gray-400 bg-gray-800 rounded border border-gray-700">
                  {formData.itemCode}
                </code>
              )}

              {/* ── Category: pill choice for new items; locked badge + explicit Change Type for existing ── */}
              {isAddingNew ? (
                /* NEW ITEM — pick the type before anything else is filled in */
                <div className="flex items-center gap-1.5 ml-1 p-1 bg-gray-800 rounded-lg border border-gray-700">
                  <button
                    type="button"
                    onClick={() => handleFormChange("category", "M")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                      formData.category === "M"
                        ? "bg-blue-500/25 text-blue-300 shadow-sm"
                        : "text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    Material
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormChange("category", "L")}
                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                      formData.category === "L"
                        ? "bg-orange-500/25 text-orange-300 shadow-sm"
                        : "text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    Labor
                  </button>
                </div>
              ) : (
                /* EXISTING ITEM — type is locked; must click "Change Type" to reclassify */
                <div className="flex items-center gap-2 ml-1">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border ${
                      isMaterialForm
                        ? "bg-blue-500/10 text-blue-300 border-blue-500/20"
                        : "bg-orange-500/10 text-orange-300 border-orange-500/20"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${isMaterialForm ? "bg-blue-400" : "bg-orange-400"}`}
                    />
                    {isMaterialForm ? "Material" : "Labor"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowChangeTypeModal(true)}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-yellow-300 hover:bg-yellow-400/10 border border-transparent hover:border-yellow-400/20 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-3 w-3" />
                    Change Type
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 text-gray-600 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* ── Change Type confirmation modal ── */}
          {showChangeTypeModal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-yellow-500/10 rounded-lg shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-1">
                      Change Item Type
                    </h3>
                    <p className="text-sm text-gray-400">
                      This item is currently a{" "}
                      <span
                        className={`font-semibold ${isMaterialForm ? "text-blue-300" : "text-orange-300"}`}
                      >
                        {isMaterialForm ? "Material" : "Labor"}
                      </span>{" "}
                      item. Switching to{" "}
                      <span
                        className={`font-semibold ${!isMaterialForm ? "text-blue-300" : "text-orange-300"}`}
                      >
                        {isMaterialForm ? "Labor" : "Material"}
                      </span>{" "}
                      will replace its pricing setup. Any vendor prices already
                      saved will be kept but may need to be reviewed.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end mt-5">
                  <button
                    type="button"
                    onClick={() => setShowChangeTypeModal(false)}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Keep as {isMaterialForm ? "Material" : "Labor"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleFormChange("category", isMaterialForm ? "L" : "M");
                      setShowChangeTypeModal(false);
                    }}
                    className="px-4 py-2 text-sm font-semibold text-white bg-yellow-500 hover:bg-yellow-400 rounded-lg transition-colors"
                  >
                    Switch to {isMaterialForm ? "Labor" : "Material"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Coloured type banner ── */}
          <div
            className={`px-5 py-2.5 flex items-center gap-2 text-xs font-medium border-b ${
              isMaterialForm
                ? "bg-blue-500/5 border-blue-500/10 text-blue-400"
                : "bg-orange-500/5 border-orange-500/10 text-orange-400"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${isMaterialForm ? "bg-blue-500" : "bg-orange-500"}`}
            />
            {isMaterialForm
              ? "Material Item — tracks vendor pricing and buy cost"
              : "Labor Item — tracks unit rate (billed) and worker rate (paid)"}
          </div>

          <div className="p-5 space-y-7">
            {/* ── Core Info (shared by both types) ── */}
            <div>
              <SectionLabel>Core Info</SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormField label="Item Code" required>
                  <input
                    type="text"
                    value={formData.itemCode}
                    onChange={(e) =>
                      handleFormChange("itemCode", e.target.value)
                    }
                    className={`${inputCls} font-mono`}
                    placeholder="ITEM-001"
                  />
                </FormField>
                <FormField label="Unit of Measure" required>
                  <Select
                    options={UOM_OPTIONS}
                    value={UOM_OPTIONS.find((o) => o.value === formData.uom)}
                    onChange={(opt) =>
                      handleFormChange("uom", opt?.value || "EA")
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </FormField>
                <FormField label="Cost Code / Section">
                  <Select
                    options={costCodeOptions}
                    value={
                      costCodeOptions.find(
                        (o) => o.value === formData.costCode,
                      ) || null
                    }
                    onChange={(opt) =>
                      handleFormChange("costCode", opt?.value || "")
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    isClearable
                    placeholder="Select section…"
                  />
                  {formData.sectionNumber && (
                    <p className="mt-1 text-xs font-mono text-orange-400/60">
                      § {formData.sectionNumber}
                    </p>
                  )}
                </FormField>

                {/* Manufacturer only relevant for material */}
                {isMaterialForm ? (
                  <FormField label="Manufacturer">
                    <input
                      type="text"
                      value={formData.manufacturer}
                      onChange={(e) =>
                        handleFormChange("manufacturer", e.target.value)
                      }
                      className={inputCls}
                      placeholder="e.g. USG"
                    />
                  </FormField>
                ) : (
                  /* Spacer so Description stays full-width on next row */
                  <div />
                )}
                <div className="md:col-span-3">
                  <FormField label="Description" required>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) =>
                        handleFormChange("description", e.target.value)
                      }
                      className={inputCls}
                      placeholder="Full item description"
                    />
                  </FormField>
                </div>
                <div className="flex items-end pb-0.5">
                  <div className="w-full p-3 rounded-lg bg-gray-800/40 border border-gray-800 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-400">
                      Active
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleFormChange("isActive", !formData.isActive)
                      }
                      className={`relative w-9 h-5 rounded-full transition-colors ${formData.isActive ? "bg-green-500" : "bg-gray-700"}`}
                    >
                      <span
                        className={`absolute w-3.5 h-3.5 bg-white rounded-full top-0.5 transition-all ${formData.isActive ? "left-[18px]" : "left-0.5"}`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════
                MATERIAL-SPECIFIC SECTIONS
            ══════════════════════════════════════════ */}
            {isMaterialForm && (
              <>
                {/* Specs & material pricing */}
                <div>
                  <SectionLabel>Specs & Pricing</SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <FormField label="Size">
                      <input
                        type="text"
                        value={formData.size}
                        onChange={(e) =>
                          handleFormChange("size", e.target.value)
                        }
                        className={inputCls}
                        placeholder="e.g. 4'×8'"
                      />
                    </FormField>
                    <FormField label="Coverage Per Unit">
                      <input
                        type="text"
                        value={formData.coveragePerUnit}
                        onChange={(e) =>
                          handleFormChange("coveragePerUnit", e.target.value)
                        }
                        readOnly={["SF", "LF"].includes(formData.uom)}
                        className={`${inputCls} ${["SF", "LF"].includes(formData.uom) ? "opacity-50 cursor-not-allowed" : ""}`}
                        placeholder="auto-computed for SF/LF"
                      />
                    </FormField>

                    <div className="md:col-span-4">
                      <FormField label="Notes">
                        <textarea
                          value={formData.itemNotes}
                          onChange={(e) =>
                            handleFormChange("itemNotes", e.target.value)
                          }
                          className={`${inputCls} resize-none`}
                          rows="2"
                          placeholder="Additional notes…"
                        />
                      </FormField>
                    </div>
                  </div>
                </div>

                {/* Vendor pricing */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <SectionLabel>Vendor Pricing</SectionLabel>
                    <button
                      type="button"
                      onClick={() => {
                        setShowVendorForm(true);
                        setEditingVendorId(null);
                        resetVendorForm();
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-3.5 w-3.5" /> Add Vendor
                    </button>
                  </div>

                  {showVendorForm && (
                    <div className="mb-4 p-4 bg-gray-950 border border-gray-800 rounded-xl">
                      <p className="text-xs font-medium text-gray-500 mb-3">
                        {editingVendorId
                          ? "Edit vendor price"
                          : "New vendor price"}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <div className="md:col-span-2">
                          <FormField label="Vendor">
                            <Select
                              options={vendorOptions}
                              value={vendorOptions.find(
                                (o) => o.value === vendorPriceForm.vendor,
                              )}
                              onChange={(opt) =>
                                setVendorPriceForm((p) => ({
                                  ...p,
                                  vendor: opt?.value || null,
                                }))
                              }
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              isDisabled={!!editingVendorId}
                              placeholder="Select vendor…"
                            />
                          </FormField>
                        </div>
                        <FormField label="Vendor Price">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-xs">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              value={vendorPriceForm.price}
                              onChange={(e) =>
                                setVendorPriceForm((p) => ({
                                  ...p,
                                  price: parseFloat(e.target.value) || 0,
                                }))
                              }
                              className="w-full rounded-lg bg-gray-800 border border-gray-700 focus:border-orange-500 text-gray-200 text-sm pl-6 pr-3 py-2 outline-none"
                            />
                          </div>
                        </FormField>
                        <FormField label="SKU / Part #">
                          <input
                            type="text"
                            value={vendorPriceForm.partNumber}
                            onChange={(e) =>
                              setVendorPriceForm((p) => ({
                                ...p,
                                partNumber: e.target.value,
                              }))
                            }
                            className="w-full rounded-lg bg-gray-800 border border-gray-700 focus:border-orange-500 text-gray-200 text-sm px-3 py-2 font-mono outline-none"
                            placeholder="Catalog #"
                          />
                        </FormField>
                        <FormField label="Notes">
                          <input
                            type="text"
                            value={vendorPriceForm.notes}
                            onChange={(e) =>
                              setVendorPriceForm((p) => ({
                                ...p,
                                notes: e.target.value,
                              }))
                            }
                            className="w-full rounded-lg bg-gray-800 border border-gray-700 focus:border-orange-500 text-gray-200 text-sm px-3 py-2 outline-none"
                            placeholder="Optional"
                          />
                        </FormField>
                        <div className="flex gap-2 pt-5">
                          <button
                            type="button"
                            onClick={handleAddVendorPrice}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            {editingVendorId ? "Update" : "Add"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowVendorForm(false);
                              setEditingVendorId(null);
                              resetVendorForm();
                            }}
                            className="px-3 py-2 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.vendorPrices.length > 0 ? (
                    <div className="space-y-2">
                      {/* Default vendor selector */}
                      <div className="flex items-center gap-3 p-3 bg-gray-950 border border-gray-800 rounded-lg">
                        <BuildingStorefrontIcon className="h-4 w-4 text-orange-400 shrink-0" />
                        <span className="text-xs font-medium text-gray-500 shrink-0">
                          Default vendor
                        </span>
                        <div className="flex-1 max-w-xs">
                          <Select
                            options={availableVendorOptions}
                            value={availableVendorOptions.find(
                              (o) => o.value === formData.defaultVendor,
                            )}
                            onChange={(opt) =>
                              handleFormChange(
                                "defaultVendor",
                                opt?.value || null,
                              )
                            }
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            isClearable
                            placeholder="Select default…"
                          />
                        </div>
                      </div>
                      {formData.vendorPrices.map((vp, idx) => {
                        const vendorId = vp.vendor?._id || vp.vendor;
                        const vendorName =
                          vp.vendor?.companyName ||
                          vendors.find((v) => v._id === vendorId)
                            ?.companyName ||
                          "Unknown";
                        const isDefault = formData.defaultVendor === vendorId;
                        return (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-3.5 rounded-xl border ${isDefault ? "bg-orange-500/5 border-orange-500/25" : "bg-gray-950/60 border-gray-800"}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {isDefault && (
                                <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-orange-400 bg-orange-500/10 border border-orange-500/20 rounded uppercase">
                                  Default
                                </span>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-200">
                                  {vendorName}
                                </p>
                                <div className="flex items-center gap-3 mt-0.5">
                                  <span className="text-sm font-mono text-green-400">
                                    ${(vp.price || 0).toFixed(3)}
                                  </span>
                                  {vp.partNumber && (
                                    <span className="text-xs text-gray-600 font-mono">
                                      #{vp.partNumber}
                                    </span>
                                  )}
                                  {vp.notes && (
                                    <span className="text-xs text-gray-600">
                                      ({vp.notes})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-3">
                              <button
                                type="button"
                                onClick={() => startEditVendor(vendorId)}
                                className="p-1.5 text-gray-600 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveVendorPrice(vendorId)
                                }
                                className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5 p-4 bg-gray-950/40 border border-dashed border-gray-800 rounded-xl">
                      <TagIcon className="h-4 w-4 text-gray-700" />
                      <p className="text-sm text-gray-700">
                        No vendor prices yet — add one above
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ══════════════════════════════════════════
                LABOR-SPECIFIC SECTIONS
            ══════════════════════════════════════════ */}
            {!isMaterialForm && (
              <>
                {/* Labor rates */}
                <div>
                  <SectionLabel>Rates</SectionLabel>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <FormField
                      label="Default Unit Rate"
                      hint="(cost with burden)"
                    >
                      <MoneyInput
                        value={formData.defaultUnitRate}
                        onChange={(e) =>
                          handleFormChange(
                            "defaultUnitRate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </FormField>
                    <FormField
                      label="Default Worker Rate"
                      hint="(paid to worker)"
                    >
                      <MoneyInput
                        value={formData.defaultCost}
                        onChange={(e) =>
                          handleFormChange(
                            "defaultCost",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </FormField>
                    <FormField
                      label="Production Rate"
                      hint={`(${formData.uom || "UOM"}/HR)`}
                    >
                      <input
                        type="number"
                        step="0.01"
                        value={formData.productionRate}
                        onChange={(e) =>
                          handleFormChange(
                            "productionRate",
                            e.target.value === ""
                              ? ""
                              : parseFloat(e.target.value) || 0,
                          )
                        }
                        className={inputCls}
                        placeholder="e.g. 120"
                      />
                    </FormField>
                    <div className="md:col-span-2">
                      <FormField label="Notes">
                        <textarea
                          value={formData.itemNotes}
                          onChange={(e) =>
                            handleFormChange("itemNotes", e.target.value)
                          }
                          className={`${inputCls} resize-none`}
                          rows="2"
                          placeholder="Additional notes…"
                        />
                      </FormField>
                    </div>
                  </div>
                </div>

                {/* Per-worker / subcontractor rates */}
                <div>
                  {showVendorForm && (
                    <div className="mb-4 p-4 bg-gray-950 border border-gray-800 rounded-xl">
                      <p className="text-xs font-medium text-gray-500 mb-3">
                        Edit rate
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div className="md:col-span-2">
                          <FormField label="Subcontractor / Worker">
                            <Select
                              options={vendorOptions}
                              value={vendorOptions.find(
                                (o) => o.value === vendorPriceForm.vendor,
                              )}
                              onChange={(opt) =>
                                setVendorPriceForm((p) => ({
                                  ...p,
                                  vendor: opt?.value || null,
                                }))
                              }
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              isDisabled={!!editingVendorId}
                              placeholder="Select…"
                            />
                          </FormField>
                        </div>
                        <FormField label="Unit Rate (billed)">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-xs">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              value={vendorPriceForm.price}
                              onChange={(e) =>
                                setVendorPriceForm((p) => ({
                                  ...p,
                                  price: parseFloat(e.target.value) || 0,
                                }))
                              }
                              className="w-full rounded-lg bg-gray-800 border border-gray-700 focus:border-orange-500 text-gray-200 text-sm pl-6 pr-3 py-2 outline-none"
                            />
                          </div>
                        </FormField>
                        <FormField label="Worker Rate (paid)">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-xs">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              value={vendorPriceForm.unitRate}
                              onChange={(e) =>
                                setVendorPriceForm((p) => ({
                                  ...p,
                                  unitRate: parseFloat(e.target.value) || 0,
                                }))
                              }
                              className="w-full rounded-lg bg-gray-800 border border-gray-700 focus:border-orange-500 text-gray-200 text-sm pl-6 pr-3 py-2 outline-none"
                            />
                          </div>
                        </FormField>
                        <div className="flex gap-2 pt-5">
                          <button
                            type="button"
                            onClick={handleAddVendorPrice}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            {editingVendorId ? "Update" : "Add"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowVendorForm(false);
                              setEditingVendorId(null);
                              resetVendorForm();
                            }}
                            className="px-3 py-2 text-xs text-gray-400 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.vendorPrices.length > 0 ? (
                    <div className="space-y-2">
                      {formData.vendorPrices.map((vp, idx) => {
                        const vendorId = vp.vendor?._id || vp.vendor;
                        const vendorName =
                          vp.vendor?.companyName ||
                          vendors.find((v) => v._id === vendorId)
                            ?.companyName ||
                          "Unknown";
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3.5 rounded-xl border bg-gray-950/60 border-gray-800"
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div>
                                <p className="text-sm font-medium text-gray-200">
                                  {vendorName}
                                </p>
                                <div className="flex items-center gap-4 mt-0.5">
                                  <span className="text-xs text-gray-500">
                                    Unit rate:{" "}
                                    <span className="font-mono text-blue-400">
                                      ${(vp.price || 0).toFixed(3)}
                                    </span>
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Worker rate:{" "}
                                    <span className="font-mono text-orange-400">
                                      ${(vp.unitRate || 0).toFixed(3)}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-3">
                              <button
                                type="button"
                                onClick={() => startEditVendor(vendorId)}
                                className="p-1.5 text-gray-600 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveVendorPrice(vendorId)
                                }
                                className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            )}

            {/* ── Chained items (shared) ── */}
            <div>
              <SectionLabel>
                Chained items{" "}
                {formData.linkedItems?.length ? (
                  <span className="rounded bg-amber-500/15 px-1.5 py-px text-[10px] text-amber-300 normal-case tracking-normal">
                    ⛓ {formData.linkedItems.length}
                  </span>
                ) : null}
              </SectionLabel>
              <ChainedItemsEditor
                value={formData.linkedItems || []}
                onChange={(v) => setFormData((p) => ({ ...p, linkedItems: v }))}
                items={items}
                selfId={editingItem}
              />
            </div>

            {/* ── History (shared) ── */}
            {formData.history?.length > 0 && (
              <div>
                <SectionLabel>History</SectionLabel>
                <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                  {[...formData.history].reverse().map((entry, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-2.5 bg-gray-950/50 rounded-lg"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0" />
                      <div>
                        <p className="text-sm text-gray-300">
                          {entry.description}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {new Date(entry.timestamp).toLocaleString()} ·{" "}
                          {entry.changedBy || "System"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Form footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-800 bg-gray-900/60">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={`flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white rounded-lg transition-colors shadow-sm ${isSaving ? "bg-gray-700 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600 shadow-orange-500/20"}`}
            >
              {isSaving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />{" "}
                  Saving…
                </>
              ) : isAddingNew ? (
                "Create Item"
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TABLE VIEW
      ══════════════════════════════════════════════════════════════════════ */}
      {!isEditing && (
        <>
          {/* Search / filter bar */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-3.5">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, search: e.target.value }))
                  }
                  placeholder="Search items…"
                  className="w-full pl-9 pr-4 py-2 rounded-lg bg-gray-950 border border-gray-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 text-gray-200 text-sm outline-none transition-colors placeholder:text-gray-700"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Show</span>
                <select
                  value={
                    filters.isActive === null
                      ? "all"
                      : filters.isActive
                        ? "active"
                        : "inactive"
                  }
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      isActive:
                        e.target.value === "all"
                          ? null
                          : e.target.value === "active",
                    }))
                  }
                  className="rounded-lg bg-gray-950 border border-gray-800 text-gray-300 text-sm px-3 py-2 outline-none focus:border-orange-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="all">All</option>
                </select>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-orange-300 bg-orange-500/10 hover:bg-orange-500/15 border border-orange-500/20 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" /> Clear filters
                </button>
              )}
              <div className="ml-auto text-sm text-gray-600">
                <span className="text-white font-semibold">
                  {filteredItems.length}
                </span>{" "}
                of {items.length}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-950/80">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">
                      Item Code
                    </th>
                    <FilterHeader
                      label="Cat"
                      filterKey="category"
                      refObj={filterCategoryRef}
                      count={filters.category.length}
                    />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <FilterHeader
                      label="Section"
                      filterKey="sectionNumber"
                      refObj={filterSectionRef}
                      count={filters.sectionNumber.length}
                      width="w-24"
                    />
                    <FilterHeader
                      label="UoM"
                      filterKey="uom"
                      refObj={filterUomRef}
                      count={filters.uom.length}
                    />
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                      Price{" "}
                      <span className="text-gray-700 font-normal normal-case tracking-normal">
                        (M) / Unit Rate (L)
                      </span>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                      Worker Rate{" "}
                      <span className="text-gray-700 font-normal normal-case tracking-normal">
                        (L only)
                      </span>
                    </th>
                    <FilterHeader
                      label="Default Vendor"
                      filterKey="vendor"
                      refObj={filterVendorRef}
                      count={filters.vendor.length}
                      center
                    />
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/60">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-4 py-14 text-center">
                        <div className="flex flex-col items-center gap-2 text-gray-700">
                          <CubeIcon className="h-8 w-8 opacity-30" />
                          <p className="text-sm">No items match your filters</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const dvp = getDefaultVendorPrice(item);
                      const isMat = item.category === "M";
                      const displayPrice = isMat
                        ? (dvp?.price ?? item.defaultCost ?? 0)
                        : item.defaultUnitRate || 0;
                      const workerRate = !isMat
                        ? (dvp?.unitRate ?? item.defaultCost ?? 0)
                        : null;

                      return (
                        <tr
                          key={item._id}
                          onClick={() => handleStartEdit(item)}
                          className={`group transition-colors hover:bg-gray-800/40 cursor-pointer ${!item.isActive ? "opacity-40" : ""}`}
                        >
                          {/* Item Code */}
                          <td className="px-4 py-3">
                            <span
                              className="text-sm font-mono text-gray-300 truncate block max-w-[7rem]"
                              title={item.itemCode}
                            >
                              {item.itemCode}
                            </span>
                          </td>
                          {/* Category */}
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide ${isMat ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-orange-500/10 text-orange-400 border border-orange-500/20"}`}
                            >
                              {isMat ? "Mat" : "Lab"}
                            </span>
                          </td>
                          {/* Description */}
                          <td className="px-4 py-3 max-w-xs">
                            <div
                              className="text-sm text-gray-200 truncate"
                              title={item.description}
                            >
                              {item.description}
                              {item.linkedItems?.length > 0 && (
                                <span
                                  className="ml-1.5 rounded bg-amber-500/15 px-1 py-px text-[10px] text-amber-300"
                                  title="Chained items come into estimates with this one"
                                >
                                  ⛓ {item.linkedItems.length}
                                </span>
                              )}
                            </div>
                            {item.size && (
                              <div className="text-xs text-gray-600 mt-0.5">
                                {item.size}
                              </div>
                            )}
                          </td>
                          {/* Section */}
                          <td className="px-3 py-3">
                            <span className="text-xs font-mono text-gray-600 block truncate max-w-[5rem]">
                              {item.sectionNumber || item.costCode?.code || "—"}
                            </span>
                          </td>
                          {/* UoM */}
                          <td className="px-3 py-3">
                            <span className="text-xs text-gray-500">
                              {item.uom}
                            </span>
                          </td>
                          {/* Price / Unit Rate */}
                          <td className="px-4 py-3 text-right">
                            <MoneyBadge
                              value={displayPrice}
                              color={isMat ? "text-green-400" : "text-blue-400"}
                            />
                            {isMat && (
                              <div className="text-[10px] text-gray-600 mt-0.5">
                                {dvp ? "vendor" : "default"}
                              </div>
                            )}
                          </td>
                          {/* Worker Rate (labor only) */}
                          <td className="px-4 py-3 text-right">
                            {!isMat ? (
                              <MoneyBadge
                                value={workerRate}
                                color="text-orange-400"
                              />
                            ) : (
                              <span className="text-gray-800 text-sm">—</span>
                            )}
                          </td>
                          {/* Default Vendor (material only) */}
                          <td className="px-4 py-3 text-center">
                            {isMat ? (
                              item.defaultVendor?.companyName ? (
                                <span className="text-sm text-gray-300">
                                  {item.defaultVendor.companyName}
                                </span>
                              ) : (
                                <span className="text-gray-700 text-sm">—</span>
                              )
                            ) : (
                              <span className="text-[11px] text-gray-700 italic">
                                n/a
                              </span>
                            )}
                          </td>
                          {/* Status */}
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${item.isActive ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-gray-800/60 text-gray-600 border-gray-700"}`}
                            >
                              {item.isActive ? "Active" : "Archived"}
                            </span>
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleActive(item);
                                }}
                                className={`p-1.5 rounded-lg transition-colors ${item.isActive ? "text-gray-600 hover:text-yellow-400 hover:bg-yellow-400/10" : "text-gray-600 hover:text-green-400 hover:bg-green-400/10"}`}
                                title={item.isActive ? "Archive" : "Restore"}
                              >
                                {item.isActive ? (
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25"
                                    />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item._id, item.itemCode);
                                }}
                                className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showUploadModal && (
        <ItemDatabaseUpload
          onUploadComplete={fetchItems}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {showPriceUpdate && (
        <VendorPriceUpdate
          vendors={vendors}
          onUpdated={fetchItems}
          onClose={() => setShowPriceUpdate(false)}
        />
      )}

      {showPriceHistory && (
        <PriceUpdateHistory
          vendors={vendors}
          onClose={() => setShowPriceHistory(false)}
        />
      )}
    </div>
  );
}
